import { gql } from '@apollo/client';

/** Everything the host wizard and manage page read from a stay (spec 016 Phase 2). */
export const STAY_HOST_LISTING_FIELDS = gql`
  fragment StayHostListingFields on Car {
    id
    name
    description
    status
    isCreated
    thumbnailUrl
    images {
      imageUrl
    }
    weekdayPrice
    weekendPrice
    holidayPrice
    minimumRentalDays
    maximumRentalDays
    latitude
    longitude
    discount {
      oneWeek
      oneMonth
      nonRefundableDiscount
    }
    tripPreferences {
      advanceNoticeHours
      tripBufferHours
    }
    priceCalendar {
      date
      price
      isAvailable
    }
    location {
      id
      address
      city
      state
      zipCode
      timezone
    }
    stayDetails {
      carId
      propertyType
      roomType
      maxGuests
      bedrooms
      beds
      bathrooms
      amenities
      houseRules {
        petsAllowed
        maxPets
        smokingAllowed
        eventsAllowed
        childrenAllowed
        infantsAllowed
        quietHoursFrom
        quietHoursTo
        additionalRules
      }
      checkInFrom
      checkInUntil
      checkOutBy
      selfCheckIn
      checkInMethod
      instantBook
      requireVerifiedId
      cleaningFee
      extraGuestFee
      extraGuestAfter
      petFee
      cancellationPolicy
      preparationDays
      stayCategories
      descriptionSpace
      descriptionGuestAccess
      descriptionOtherNotes
      descriptionNeighborhood
      listingStatus
      snoozedUntil
      wizardStep
      updatedAt
    }
  }
`;

export const MY_STAYS = gql`
  query MyStays($status: StayListingStatus, $page: Int, $limit: Int) {
    myStays(status: $status, page: $page, limit: $limit) {
      items {
        ...StayHostListingFields
      }
      totalCount
      hasMore
      page
      limit
      totalPages
    }
  }
  ${STAY_HOST_LISTING_FIELDS}
`;

export const MY_STAY = gql`
  query MyStay($id: ID!) {
    myStay(id: $id) {
      ...StayHostListingFields
    }
  }
  ${STAY_HOST_LISTING_FIELDS}
`;

/** Door code and Wi-Fi: host only, never on the listing type. */
export const STAY_PRIVATE_DETAILS = gql`
  query StayPrivateDetails($id: ID!) {
    stayPrivateDetails(id: $id) {
      checkInInstructions
      wifiName
      wifiPassword
    }
  }
`;

export const STAY_PUBLISH_CHECKLIST = gql`
  query StayPublishChecklist($id: ID!) {
    stayPublishChecklist(id: $id) {
      code
      message
    }
  }
`;

export const CREATE_STAY_DRAFT = gql`
  mutation CreateStayDraft($input: CreateStayDraftInput!) {
    createStayDraft(input: $input) {
      ...StayHostListingFields
    }
  }
  ${STAY_HOST_LISTING_FIELDS}
`;

export const UPDATE_STAY = gql`
  mutation UpdateStay($id: ID!, $input: UpdateStayInput!) {
    updateStay(id: $id, input: $input) {
      ...StayHostListingFields
    }
  }
  ${STAY_HOST_LISTING_FIELDS}
`;

export const UPDATE_STAY_PHOTOS = gql`
  mutation UpdateStayPhotos($id: ID!, $imageUrls: [String!]!) {
    updateStayPhotos(id: $id, imageUrls: $imageUrls) {
      ...StayHostListingFields
    }
  }
  ${STAY_HOST_LISTING_FIELDS}
`;

export const PUBLISH_STAY = gql`
  mutation PublishStay($id: ID!) {
    publishStay(id: $id) {
      ...StayHostListingFields
    }
  }
  ${STAY_HOST_LISTING_FIELDS}
`;

export const UNLIST_STAY = gql`
  mutation UnlistStay($id: ID!) {
    unlistStay(id: $id) {
      ...StayHostListingFields
    }
  }
  ${STAY_HOST_LISTING_FIELDS}
`;

export const SNOOZE_STAY = gql`
  mutation SnoozeStay($id: ID!, $until: DateTime!) {
    snoozeStay(id: $id, until: $until) {
      ...StayHostListingFields
    }
  }
  ${STAY_HOST_LISTING_FIELDS}
`;

export const DELETE_STAY_DRAFT = gql`
  mutation DeleteStayDraft($id: ID!) {
    deleteStayDraft(id: $id)
  }
`;

// ─── guest side (spec 016 Phase 3) ──────────────────────────────────────────

/** What a card in search, home and wishlists shows. The location is masked for guests. */
export const STAY_CARD_FIELDS = gql`
  fragment StayCardFields on Car {
    id
    name
    weekdayPrice
    averageRating
    totalRatings
    isFavorite
    images {
      imageUrl
    }
    location {
      id
      city
      state
      timezone
    }
    approximateLocation {
      latitude
      longitude
      radiusMeters
    }
    stayDetails {
      carId
      propertyType
      roomType
      maxGuests
      bedrooms
      beds
      bathrooms
      instantBook
      stayCategories
    }
  }
`;

export const STAY_QUOTE_FIELDS = gql`
  fragment StayQuoteFields on StayPriceQuote {
    nights
    pricePerNight
    nightlySubtotal
    weeklyDiscountAmount
    monthlyDiscountAmount
    cleaningFee
    extraGuestFee
    petFee
    guestServiceFee
    guestServiceFeeRate
    occupancyTax
    totalPrice
  }
`;

export const SEARCH_STAYS = gql`
  query SearchStays($input: SearchStaysInput!) {
    searchStays(input: $input) {
      total
      page
      limit
      hasMore
      items {
        distanceMiles
        listing {
          ...StayCardFields
        }
        quote {
          ...StayQuoteFields
        }
      }
    }
  }
  ${STAY_CARD_FIELDS}
  ${STAY_QUOTE_FIELDS}
`;

export const STAY_DETAIL = gql`
  query StayDetail($id: ID!) {
    stay(id: $id) {
      ...StayCardFields
      description
      weekendPrice
      minimumRentalDays
      maximumRentalDays
      cleanlinessAvg
      accuracyAvg
      communicationAvg
      checkInAvg
      locationAvg
      valueAvg
      discount {
        oneWeek
        oneMonth
      }
      owner {
        id
        displayName
        firstName
        createdAt
      }
      stayDetails {
        carId
        propertyType
        roomType
        maxGuests
        bedrooms
        beds
        bathrooms
        instantBook
        stayCategories
        amenities
        houseRules {
          petsAllowed
          maxPets
          smokingAllowed
          eventsAllowed
          childrenAllowed
          infantsAllowed
          quietHoursFrom
          quietHoursTo
          additionalRules
        }
        checkInFrom
        checkInUntil
        checkOutBy
        selfCheckIn
        checkInMethod
        cleaningFee
        extraGuestFee
        extraGuestAfter
        petFee
        cancellationPolicy
        descriptionSpace
        descriptionGuestAccess
        descriptionOtherNotes
        descriptionNeighborhood
        accessibilityFeatures
      }
    }
  }
  ${STAY_CARD_FIELDS}
`;

export const STAY_CALENDAR = gql`
  query StayCalendar($id: ID!, $from: String, $to: String, $excludeBookingId: ID) {
    stayCalendar(id: $id, from: $from, to: $to, excludeBookingId: $excludeBookingId) {
      timezone
      blockedNights
      minimumNights
      maximumNights
      checkInFrom
      checkOutBy
      from
      to
    }
  }
`;

export const STAY_QUOTE = gql`
  query StayQuote($id: ID!, $input: StayQuoteInput!) {
    stayQuote(id: $id, input: $input) {
      ...StayQuoteFields
    }
  }
  ${STAY_QUOTE_FIELDS}
`;

export const STAY_REVIEWS = gql`
  query StayReviews($stayId: ID!, $page: Int, $limit: Int) {
    stayReviews(stayId: $stayId, page: $page, limit: $limit) {
      total
      hasMore
      summary {
        averageRating
        totalRatings
        cleanlinessAvg
        accuracyAvg
        checkInAvg
        communicationAvg
        locationAvg
        valueAvg
      }
      items {
        guestName
        tripDate
        rating {
          id
          stayGuestRating {
            overall
            publicReview
          }
        }
      }
    }
  }
`;

/** Saved places (the account's favorites, kept to places to stay on this site). */
export const MY_WISHLIST = gql`
  query MyWishlist {
    getMyFavorites {
      ...StayCardFields
    }
  }
  ${STAY_CARD_FIELDS}
`;

// ─── Phase 4: checkout, requests, trips and cancellation (spec 016 W17 to W21) ───

export const STAY_BOOKING_FIELDS = gql`
  fragment StayBookingFields on Booking {
    id
    shortId
    status
    productType
    startAt
    endAt
    createdAt
    requestExpiresAt
    requestMessage
    hostRespondedAt
    stayInstructionsAvailableAt
    cancellationPolicySnapshot
    cancellationReason
    cancelledInitiatedAt
    guestCounts {
      adults
      children
      infants
      pets
    }
    receipt {
      totalPrice
      balance
      cancellationPenalty
      last4digitOfCard
      nights
      pricePerNight
      nightlySubtotal
      weeklyDiscountAmount
      monthlyDiscountAmount
      cleaningFee
      extraGuestFee
      petFee
      guestServiceFee
      guestServiceFeeRate
      hostServiceFee
      hostServiceFeeRate
      occupancyTax
    }
    car {
      id
      name
      images {
        imageUrl
      }
      location {
        id
        address
        city
        state
        zipCode
        timezone
        latitude
        longitude
      }
      stayDetails {
        carId
        propertyType
        roomType
        maxGuests
        checkInFrom
        checkOutBy
        houseRules {
          petsAllowed
          maxPets
          smokingAllowed
          eventsAllowed
          quietHoursFrom
          quietHoursTo
          additionalRules
        }
      }
    }
    guest {
      id
      displayName
      firstName
    }
    host {
      id
      displayName
      firstName
    }
  }
`;

export const CREATE_STAY_BOOKING_INTENT = gql`
  mutation CreateStayBookingIntent($input: CreateStayBookingInput!) {
    createStayBookingIntent(input: $input) {
      isRequest
      booking {
        ...StayBookingFields
      }
      quote {
        ...StayQuoteFields
      }
    }
  }
  ${STAY_BOOKING_FIELDS}
  ${STAY_QUOTE_FIELDS}
`;

export const CREATE_STAY_BOOKING_REQUEST = gql`
  mutation CreateStayBookingRequest($input: CreateStayBookingInput!) {
    createStayBookingRequest(input: $input) {
      isRequest
      booking {
        ...StayBookingFields
      }
      quote {
        ...StayQuoteFields
      }
    }
  }
  ${STAY_BOOKING_FIELDS}
  ${STAY_QUOTE_FIELDS}
`;

export const SYNC_STAY_BOOKING_REQUEST = gql`
  mutation SyncStayBookingRequest($bookingId: ID!) {
    syncStayBookingRequest(bookingId: $bookingId) {
      id
      status
    }
  }
`;

export const STAY_BOOKING_AS_GUEST = gql`
  query StayBookingAsGuest($id: String!) {
    getBookingAsGuest(id: $id) {
      ...StayBookingFields
    }
  }
  ${STAY_BOOKING_FIELDS}
`;

export const STAY_BOOKING_AS_HOST = gql`
  query StayBookingAsHost($id: String!) {
    getBookingAsHost(id: $id) {
      ...StayBookingFields
    }
  }
  ${STAY_BOOKING_FIELDS}
`;

export const MY_STAY_TRIPS = gql`
  query MyStayTrips {
    myBookings {
      ...StayBookingFields
    }
  }
  ${STAY_BOOKING_FIELDS}
`;

export const MY_STAY_RESERVATIONS = gql`
  query MyStayReservations {
    bookingsForMyListings {
      ...StayBookingFields
    }
  }
  ${STAY_BOOKING_FIELDS}
`;

export const ACCEPT_BOOKING_REQUEST = gql`
  mutation AcceptBookingRequest($bookingId: ID!) {
    acceptBookingRequest(bookingId: $bookingId) {
      ...StayBookingFields
    }
  }
  ${STAY_BOOKING_FIELDS}
`;

export const DECLINE_BOOKING_REQUEST = gql`
  mutation DeclineBookingRequest($bookingId: ID!, $reason: String) {
    declineBookingRequest(bookingId: $bookingId, reason: $reason) {
      ...StayBookingFields
    }
  }
  ${STAY_BOOKING_FIELDS}
`;

export const WITHDRAW_BOOKING_REQUEST = gql`
  mutation WithdrawBookingRequest($bookingId: ID!) {
    withdrawBookingRequest(bookingId: $bookingId) {
      ...StayBookingFields
    }
  }
  ${STAY_BOOKING_FIELDS}
`;

export const STAY_CANCELLATION_PREVIEW = gql`
  query StayCancellationPreview($bookingId: ID!) {
    stayCancellationPreview(bookingId: $bookingId) {
      policy
      rule
      cancelledBy
      status
      fullRefund
      totalPaid
      refundAmount
      nonRefundableAmount
      lodgingRefund
      cleaningFeeRefund
      petFeeRefund
      guestServiceFeeRefund
      occupancyTaxRefund
      nonRefundableNights
      hostPenalty
    }
  }
`;

export const CANCEL_STAY_BOOKING = gql`
  mutation CancelStayBooking($bookingId: ID!, $reason: String) {
    cancelStayBooking(bookingId: $bookingId, reason: $reason) {
      ...StayBookingFields
    }
  }
  ${STAY_BOOKING_FIELDS}
`;

/** After the bank check on an instant stay (same server check as the car checkout). */
export const VERIFY_STAY_PAYMENT = gql`
  mutation VerifyStayPayment($bookingId: String!, $paymentIntentId: String) {
    verifyBookingPayment(bookingId: $bookingId, paymentIntentId: $paymentIntentId) {
      id
      status
    }
  }
`;

// ─── Changing a confirmed stay (spec 016 B31, B32) ───

export const STAY_MODIFICATION_QUOTE = gql`
  query StayModificationQuote($input: ModifyStayInput!) {
    stayModificationQuote(input: $input) {
      checkIn
      checkOut
      oldTotal
      newTotal
      difference
      chargeAmount
      refundAmount
      keptAmount
      quote {
        ...StayQuoteFields
      }
    }
  }
  ${STAY_QUOTE_FIELDS}
`;

export const MODIFY_STAY_BOOKING = gql`
  mutation ModifyStayBooking($input: ModifyStayInput!) {
    modifyStayBooking(input: $input) {
      status
      clientSecret
      paymentIntentId
      errorMessage
      chargeAmount
      refundAmount
      booking {
        ...StayBookingFields
      }
    }
  }
  ${STAY_BOOKING_FIELDS}
`;

export const CONFIRM_STAY_MODIFICATION = gql`
  mutation ConfirmStayModification($input: ConfirmStayModificationInput!) {
    confirmStayModification(input: $input) {
      status
      clientSecret
      paymentIntentId
      errorMessage
      chargeAmount
      refundAmount
      booking {
        ...StayBookingFields
      }
    }
  }
  ${STAY_BOOKING_FIELDS}
`;

/** Check-in details: the host any time, the guest 48 hours before check-in (spec 016 B37). */
export const REVEAL_STAY_INSTRUCTIONS = gql`
  mutation RevealStayInstructions($bookingId: ID!) {
    revealStayInstructions(bookingId: $bookingId) {
      address
      checkInFrom
      checkInUntil
      checkOutBy
      selfCheckIn
      checkInMethod
      checkInInstructions
      wifiName
      wifiPassword
    }
  }
`;

/** The host's Today page (spec 016 W23). */
export const HOST_TODAY = gql`
  query HostToday {
    hostToday {
      pendingRequests {
        ...StayBookingFields
      }
      checkingOut {
        ...StayBookingFields
      }
      currentlyHosting {
        ...StayBookingFields
      }
      arrivingToday {
        ...StayBookingFields
      }
      upcoming {
        ...StayBookingFields
      }
      pendingReviews {
        ...StayBookingFields
      }
    }
  }
  ${STAY_BOOKING_FIELDS}
`;

/** Whether this side can still review the stay, and the reviews that show (spec 016 W22). */
export const STAY_REVIEW_STATE = gql`
  query StayReviewState($bookingId: ID!) {
    canRateBooking(bookingId: $bookingId) {
      canGuestRate
      canHostRate
      guestRatingDeadline
      hostRatingDeadline
      reason
    }
    getTripRating(bookingId: $bookingId) {
      id
      guestRatedAt
      hostRatedAt
      stayGuestRating {
        overall
        cleanliness
        accuracy
        checkIn
        communication
        location
        value
        publicReview
      }
      stayHostRating {
        overall
        cleanliness
        communication
        houseRules
        wouldHostAgain
        publicReview
      }
    }
  }
`;

export const SUBMIT_STAY_GUEST_RATING = gql`
  mutation SubmitStayGuestRating($input: SubmitStayGuestRatingInput!) {
    submitStayGuestRating(input: $input) {
      id
      guestRatedAt
    }
  }
`;

export const SUBMIT_STAY_HOST_RATING = gql`
  mutation SubmitStayHostRating($input: SubmitStayHostRatingInput!) {
    submitStayHostRating(input: $input) {
      id
      hostRatedAt
    }
  }
`;

/** Reviews guests wrote about the host's places (spec 016 W22). */
export const HOST_STAY_REVIEWS = gql`
  query HostStayReviews($hostId: ID!, $limit: Int, $offset: Int) {
    getHostRatings(hostId: $hostId, limit: $limit, offset: $offset) {
      totalCount
      summary {
        averageRating
        totalRatings
      }
      reviews {
        guestName
        guestAvatarUrl
        tripDate
        carName
        rating {
          id
          carId
          guestRatedAt
          stayGuestRating {
            overall
            cleanliness
            accuracy
            checkIn
            communication
            location
            value
            publicReview
          }
        }
      }
    }
  }
`;

/** Whether a Resolution Center request can be opened for this stay (spec 016 W25). */
export const CAN_OPEN_STAY_REQUEST = gql`
  query CanOpenStayRequest($bookingId: String!) {
    canReportDamage(bookingId: $bookingId) {
      canReport
      reason
      deadline
    }
  }
`;

export const OPEN_STAY_REQUEST = gql`
  mutation OpenStayRequest($input: CreateDamageClaimInput!) {
    createDamageClaim(input: $input) {
      id
      claimNum
    }
  }
`;

export const STAY_REQUEST_PHOTO_URL = gql`
  mutation StayRequestPhotoUrl($fileType: String!, $claimId: String!, $photoType: String!) {
    getPresignedUrlForDamageClaim(fileType: $fileType, claimId: $claimId, photoType: $photoType) {
      url
      key
    }
  }
`;

/** A guest's question to the host before booking (spec 016 B40, W27). */
export const CREATE_INQUIRY_CHAT = gql`
  mutation CreateInquiryChat($input: CreateInquiryChatInput!) {
    createInquiryChat(input: $input) {
      chatId
      success
      message
    }
  }
`;
